import { useState, useEffect } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  ArrowLeft, Send, GraduationCap, Shield, Monitor,
  Accessibility, UserCheck, BookOpen
} from "lucide-react";

// ============================================
// CYBERSECURITY QUESTIONS
// ============================================
const CybersecurityForm = ({ form, setForm }) => (
  <div className="space-y-6">
    {/* Intro */}
    <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
      <h3 className="font-semibold text-slate-900 mb-2">About the Program</h3>
      <p className="text-sm text-slate-600 mb-3">100% grant-funded training for neurodivergent adults!</p>
      <div className="grid sm:grid-cols-2 gap-4 text-sm">
        <div className="bg-white rounded-lg p-3 border border-blue-100">
          <p className="font-semibold text-brand-navy mb-1">Academy (All states)</p>
          <ul className="text-slate-600 space-y-1 text-xs">
            <li>Free 12-week program</li>
            <li>Security+ certification prep</li>
            <li>CMMC and Career Readiness Training</li>
            <li>You only pay exam fee (waived for IL residents)</li>
          </ul>
        </div>
        <div className="bg-white rounded-lg p-3 border border-blue-100">
          <p className="font-semibold text-brand-navy mb-1">Apprenticeship (IL only)</p>
          <ul className="text-slate-600 space-y-1 text-xs">
            <li>Everything in Academy PLUS</li>
            <li>Paid wages during training</li>
            <li>Job placement included</li>
            <li>Exam fee covered</li>
          </ul>
        </div>
      </div>
    </div>

    <CheckboxGroup label="Communication preferences (check all that apply)" options={[
      "Email", "Text/SMS", "Phone calls", "Video calls", "Slack/Discord", "I prefer written communication only"
    ]} value={form.communication_preferences || []} onChange={(v) => setForm(p => ({ ...p, communication_preferences: v }))} name="communication_preferences" />

    <SelectField label="Rate your current technical comfort level" options={[
      { value: "beginner", label: "Beginner - I'm new to technology" },
      { value: "basic", label: "Basic - I can use a computer and browse the internet" },
      { value: "intermediate", label: "Intermediate - I'm comfortable with various software" },
      { value: "advanced", label: "Advanced - I have IT/tech experience" },
    ]} value={form.technical_comfort} onChange={(v) => setForm(p => ({ ...p, technical_comfort: v }))} name="technical_comfort" />

    <TextareaField label="Have you completed any certifications?" value={form.certifications_completed} onChange={(v) => setForm(p => ({ ...p, certifications_completed: v }))} placeholder="List any certifications you have..." name="certifications_completed" />

    <CheckboxGroup label="Do you have experience with any of the following?" options={[
      "Windows OS", "Linux/Unix", "Networking basics", "Cloud services (AWS, Azure, GCP)",
      "Programming/scripting", "Help desk/IT support", "Cybersecurity concepts", "None of the above"
    ]} value={form.technical_experience || []} onChange={(v) => setForm(p => ({ ...p, technical_experience: v }))} name="technical_experience" />

    <CheckboxGroup label="What's your PRIMARY goal for this program? (select as many as applicable)" options={[
      "Get Security+ certified", "Start a cybersecurity career", "Advance in current IT role",
      "Learn to protect my own digital life", "Explore if cybersecurity is right for me",
      "Build technical confidence", "Meet others in the field"
    ]} value={form.primary_goals || []} onChange={(v) => setForm(p => ({ ...p, primary_goals: v }))} name="primary_goals" />

    <SelectField label="Our program meets Monday-Thursday, 9:00 AM - 12:00 PM CST via Google Meet. Can you commit to this schedule? *" options={[
      { value: "yes", label: "Yes, I can commit to this schedule" },
      { value: "mostly", label: "Mostly, but I may miss occasionally" },
      { value: "need_accommodations", label: "I need schedule accommodations" },
      { value: "no", label: "No, this schedule doesn't work for me" },
    ]} value={form.schedule_commitment} onChange={(v) => setForm(p => ({ ...p, schedule_commitment: v }))} name="schedule_commitment" required />

    <SelectField label="Outside of live sessions, can you commit 7-10 additional hours/week for labs, studying, and assignments? *" options={[
      { value: "yes", label: "Yes, I can commit to this" },
      { value: "some", label: "I can commit to some, but not all" },
      { value: "unsure", label: "I'm not sure yet" },
      { value: "no", label: "No, that's too much time" },
    ]} value={form.study_commitment} onChange={(v) => setForm(p => ({ ...p, study_commitment: v }))} name="study_commitment" required />

    <TextareaField label="Why are you interested in cybersecurity specifically?" value={form.why_cybersecurity} onChange={(v) => setForm(p => ({ ...p, why_cybersecurity: v }))} placeholder="Tell us what draws you to cybersecurity..." maxLength={2000} name="why_cybersecurity" required />

    <CheckboxGroup label="What excites you most about this field? (choose all that apply)" options={[
      "Protecting people and organizations", "Solving puzzles and problems",
      "High demand for cybersecurity professionals", "Continuous learning opportunities",
      "Working with cutting-edge technology", "Making the digital world safer",
      "Good salary potential", "Remote work opportunities"
    ]} value={form.what_excites || []} onChange={(v) => setForm(p => ({ ...p, what_excites: v }))} name="what_excites" />

    <TextareaField label="What's your biggest concern or barrier to starting in Cybersecurity?" value={form.biggest_barrier} onChange={(v) => setForm(p => ({ ...p, biggest_barrier: v }))} placeholder="Think about what could make it difficult for you to start your career in cybersecurity..." maxLength={2000} name="biggest_barrier" />

    <SelectField label="Have you tried learning cybersecurity before?" options={[
      { value: "no", label: "No, this would be my first time" },
      { value: "self_study", label: "Yes, through self-study" },
      { value: "online_course", label: "Yes, through an online course" },
      { value: "bootcamp", label: "Yes, through a bootcamp or program" },
      { value: "college", label: "Yes, through college/university" },
    ]} value={form.prior_learning} onChange={(v) => setForm(p => ({ ...p, prior_learning: v }))} name="prior_learning" />

    <TextareaField label="Tell us a bit about the work you've done previously" value={form.work_history} onChange={(v) => setForm(p => ({ ...p, work_history: v }))} placeholder="Any work experience, volunteer work, or projects..." maxLength={2000} name="work_history" />

    <CheckboxGroup label="What type of support would help you succeed? (select all that apply)" options={[
      "Extra time on assignments", "Written instructions", "1-on-1 tutoring",
      "Study groups", "Recorded lectures", "Break reminders",
      "Quiet study environment tips", "Career coaching", "Mental health support"
    ]} value={form.support_needed || []} onChange={(v) => setForm(p => ({ ...p, support_needed: v }))} name="support_needed" />

    <TextareaField label="Is there anything else you'd like us to know?" value={form.additional_info} onChange={(v) => setForm(p => ({ ...p, additional_info: v }))} placeholder="Optional..." name="additional_info" />
  </div>
);

// ============================================
// QUALITY ASSURANCE QUESTIONS
// ============================================
const QualityAssuranceForm = ({ form, setForm }) => (
  <div className="space-y-6">
    <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
      <h3 className="font-semibold text-slate-900 mb-2">About the Program</h3>
      <p className="text-sm text-slate-600 mb-3">This virtual 6-week program runs Monday-Friday, 9:00 AM - 5:00 PM CST, approximately 6 hours daily.</p>
      <div className="bg-white rounded-lg p-3 border border-blue-100">
        <ul className="text-slate-600 space-y-1 text-xs">
          <li>Free QA training for neurodivergent adults</li>
          <li>Hands-on software quality assurance analysis</li>
          <li>Career readiness and professional development</li>
        </ul>
      </div>
    </div>

    <SelectField label="Are you available to work the program schedule (Mon-Fri, 9 AM - 5 PM CST, ~6 hrs/day for 6 weeks)? *" options={[
      { value: "yes", label: "Yes, I can commit to this schedule" },
      { value: "no", label: "No, this schedule doesn't work for me" },
    ]} value={form.schedule_available} onChange={(v) => setForm(p => ({ ...p, schedule_available: v }))} name="schedule_available" required />

    {form.schedule_available === "no" && (
      <TextareaField label="What obligations may get in the way?" value={form.schedule_conflicts} onChange={(v) => setForm(p => ({ ...p, schedule_conflicts: v }))} placeholder="Please describe your scheduling conflicts..." name="schedule_conflicts" />
    )}

    <TextareaField label="What do you hope to gain from this program? *" value={form.program_hopes} onChange={(v) => setForm(p => ({ ...p, program_hopes: v }))} placeholder="Tell us what you hope to gain..." maxLength={2000} name="program_hopes" required />

    <CheckboxGroup label="What is it about this program that interests you? (select all that apply) *" options={[
      "Building skills for a future career",
      "Interest in learning more about QA-type work",
      "Enjoy detail-oriented work",
      "Looking for a technical job using a computer",
      "Professional development",
      "Meet new people",
      "Interested in Aspiritech as a company",
      "Unsure or Other"
    ]} value={form.program_interests || []} onChange={(v) => setForm(p => ({ ...p, program_interests: v }))} name="program_interests" />

    {(form.program_interests || []).includes("Unsure or Other") && (
      <TextareaField label="Please provide more information on what interests you" value={form.other_interest_detail} onChange={(v) => setForm(p => ({ ...p, other_interest_detail: v }))} placeholder="Tell us more..." name="other_interest_detail" />
    )}

    <TextareaField label="Describe your understanding of what Quality Assurance work is and/or the type of tasks a QA Analyst does. *" value={form.qa_understanding} onChange={(v) => setForm(p => ({ ...p, qa_understanding: v }))} placeholder='If you are uncertain, you can enter "none."' maxLength={2000} name="qa_understanding" required />

    <TextareaField label="How do you use technology in your day-to-day life? *" value={form.daily_tech_use} onChange={(v) => setForm(p => ({ ...p, daily_tech_use: v }))} placeholder="Describe your daily technology usage..." maxLength={2000} name="daily_tech_use" required />

    <CheckboxGroup label="What is your preferred method of communication? (check all that apply) *" options={[
      "Written", "Verbal", "Messaging/Emailing", "Face-to-Face/In-Person"
    ]} value={form.communication_methods || []} onChange={(v) => setForm(p => ({ ...p, communication_methods: v }))} name="communication_methods" />

    <TextareaField label="What are your career goals within the next 5 years? *" value={form.career_goals} onChange={(v) => setForm(p => ({ ...p, career_goals: v }))} placeholder="Where do you see yourself in 5 years?" maxLength={2000} name="career_goals" required />

    <TextareaField label="What skills or training do you currently possess that would make you a good fit for this QA training program? *" value={form.relevant_skills} onChange={(v) => setForm(p => ({ ...p, relevant_skills: v }))} placeholder="Any skills, traits, or experience that are relevant..." maxLength={2000} name="relevant_skills" required />

    <SelectField label="What timezone do you live in? *" options={[
      { value: "eastern", label: "Eastern (ET)" },
      { value: "central", label: "Central (CT)" },
      { value: "mountain", label: "Mountain (MT)" },
      { value: "pacific", label: "Pacific (PT)" },
      { value: "other", label: "Other" },
    ]} value={form.timezone} onChange={(v) => setForm(p => ({ ...p, timezone: v }))} name="timezone" required />

    <TextareaField label="Is there any additional information you'd like to share about why you'd be a good candidate?" value={form.additional_info} onChange={(v) => setForm(p => ({ ...p, additional_info: v }))} placeholder="Optional..." name="additional_info" />
  </div>
);

// ============================================
// IT FOUNDATIONS QUESTIONS
// ============================================
const ITFoundationsForm = ({ form, setForm }) => (
  <div className="space-y-6">
    <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
      <h3 className="font-semibold text-slate-900 mb-2">About the Program</h3>
      <p className="text-sm text-slate-600">This program teaches you how computers work from the ground up. Answer the following questions to the best of your ability.</p>
    </div>

    <TextareaField label="1. What specifically interests you about learning how computers work? *" value={form.interest_in_computers} onChange={(v) => setForm(p => ({ ...p, interest_in_computers: v }))} placeholder="Tell us what draws you to understanding technology..." maxLength={2000} name="interest_in_computers" required />

    <TextareaField label='2. Have you ever wondered how a specific piece of technology (like a phone, a website, or a game) functions "under the hood"? If so, what was it, and what did you try to find out?' value={form.tech_curiosity} onChange={(v) => setForm(p => ({ ...p, tech_curiosity: v }))} placeholder="Describe a time you were curious about how something works..." maxLength={2000} name="tech_curiosity" />

    <TextareaField label="3. What is one technology problem you solved for yourself or someone else? (It can be as simple as fixing a TV remote or changing a setting on a phone.)" value={form.tech_problem_solved} onChange={(v) => setForm(p => ({ ...p, tech_problem_solved: v }))} placeholder="Describe the problem and how you solved it..." maxLength={2000} name="tech_problem_solved" />

    <TextareaField label="4. If you complete this program, what is your ideal outcome? (e.g., getting a specific type of job, fixing your own devices, learning a new hobby, etc.)" value={form.ideal_outcome} onChange={(v) => setForm(p => ({ ...p, ideal_outcome: v }))} placeholder="What do you hope to achieve?" maxLength={2000} name="ideal_outcome" />

    <TextareaField label="5. Scenario: You plug in a lamp and turn the switch, but the light does not turn on. Please list 3-5 logical steps you would take to figure out the cause of the problem. *" value={form.lamp_scenario} onChange={(v) => setForm(p => ({ ...p, lamp_scenario: v }))} placeholder="List your troubleshooting steps..." maxLength={2000} name="lamp_scenario" required />

    <TextareaField label="6. When you are learning something new, which method works best for you? (e.g., reading a manual, watching a video, having someone show you, or experimenting on your own)" value={form.learning_method} onChange={(v) => setForm(p => ({ ...p, learning_method: v }))} placeholder="Describe your preferred learning method..." maxLength={2000} name="learning_method" />

    <TextareaField label="7. Working in IT often involves encountering errors that take time to fix. How do you typically react when you get stuck on a problem for a long time?" value={form.stuck_reaction} onChange={(v) => setForm(p => ({ ...p, stuck_reaction: v }))} placeholder="E.g., I take a break, I research online, I get frustrated and stop, I ask for help immediately..." maxLength={2000} name="stuck_reaction" />

    <TextareaField label="8. What is one thing that usually makes it difficult for you to learn in a classroom or group setting?" value={form.learning_difficulty} onChange={(v) => setForm(p => ({ ...p, learning_difficulty: v }))} placeholder="This helps us understand how to best support you..." maxLength={2000} name="learning_difficulty" />

    <div className="space-y-1.5">
      <Label className="text-slate-700 font-medium text-sm">9. Please look at the two strings of text below and identify the single difference between them: *</Label>
      <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 font-mono text-sm space-y-1">
        <p className="text-slate-800">String A: <strong>8902-Alpha-22x-G7</strong></p>
        <p className="text-slate-800">String B: <strong>8902-Alpha-22z-G7</strong></p>
      </div>
      <Textarea
        value={form.string_difference || ""}
        onChange={(e) => setForm(p => ({ ...p, string_difference: e.target.value }))}
        placeholder="What is the difference?"
        className="input-base min-h-[60px]"
        data-testid="field-string_difference"
      />
    </div>

    <TextareaField label="10. Have you taken any IT related courses? If so, please describe." value={form.it_courses} onChange={(v) => setForm(p => ({ ...p, it_courses: v }))} placeholder="Online courses, bootcamps, certifications, self-study, etc." name="it_courses" />

    <TextareaField label="11. Do you have any professional IT work experience? If so, please describe." value={form.it_work_experience} onChange={(v) => setForm(p => ({ ...p, it_work_experience: v }))} placeholder="Any work, volunteer, or project experience in IT..." name="it_work_experience" />

    <TextareaField label="12. Describe your best and/or worst experiences receiving technical support from someone else (personal or professional), AND explain why they were best or worst." value={form.tech_support_experience} onChange={(v) => setForm(p => ({ ...p, tech_support_experience: v }))} placeholder="What made the experience good or bad?" maxLength={2000} name="tech_support_experience" />

    <TextareaField label="13. What kind of accommodations might you need/want/help you?" value={form.accommodations_needed} onChange={(v) => setForm(p => ({ ...p, accommodations_needed: v }))} placeholder="Any accommodations that would help you succeed..." name="accommodations_needed" />
  </div>
);

// ============================================
// ACCESSIBILITY QUESTIONS
// ============================================
const AccessibilityForm = ({ form, setForm }) => (
  <div className="space-y-6">
    <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
      <h3 className="font-semibold text-slate-900 mb-2">About the Program</h3>
      <p className="text-sm text-slate-600">Digital accessibility ensures technology works for everyone. This program teaches you to test and improve digital experiences for people with disabilities.</p>
    </div>

    <TextareaField label="1. Digital accessibility is about ensuring technology works for everyone, including people with disabilities. Why does this specific mission appeal to you? *" value={form.mission_appeal} onChange={(v) => setForm(p => ({ ...p, mission_appeal: v }))} placeholder="Tell us why this mission resonates with you..." maxLength={2000} name="mission_appeal" required />

    <TextareaField label="2. Describe a time you (or someone you know) felt frustrated because a website or app was difficult or confusing to use. What was the specific problem?" value={form.frustration_experience} onChange={(v) => setForm(p => ({ ...p, frustration_experience: v }))} placeholder="Describe the experience and the specific problem..." maxLength={2000} name="frustration_experience" />

    <TextareaField label="3. Imagine you are watching a video with the sound off. What features would you need to understand what is happening?" value={form.video_no_sound} onChange={(v) => setForm(p => ({ ...p, video_no_sound: v }))} placeholder="Think about what accommodations would help you follow along..." maxLength={2000} name="video_no_sound" />

    <TextareaField label='4. Scenario: A website instructs the user to "Click the green button to continue." Why might this instruction be a problem for some users? *' value={form.green_button_scenario} onChange={(v) => setForm(p => ({ ...p, green_button_scenario: v }))} placeholder="Think about different types of users..." maxLength={2000} name="green_button_scenario" required />

    <TextareaField label="5. Scenario: You are trying to buy a concert ticket, but the timer runs out before you can find your credit card. How would you fix this feature to make it more accessible?" value={form.timer_scenario} onChange={(v) => setForm(p => ({ ...p, timer_scenario: v }))} placeholder="How would you improve this experience?" maxLength={2000} name="timer_scenario" />

    <TextareaField label="6. If you could not use a mouse and only had a keyboard, how would you expect to move around a webpage? (e.g., Tab key, arrow keys, etc.)" value={form.keyboard_navigation} onChange={(v) => setForm(p => ({ ...p, keyboard_navigation: v }))} placeholder="Describe how you'd navigate..." maxLength={2000} name="keyboard_navigation" />

    <div className="space-y-1.5">
      <Label className="text-slate-700 font-medium text-sm">7. You are testing a webpage and find that the font is too small to read. Which of the following bug reports is better, and why? *</Label>
      <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 text-sm space-y-2">
        <p className="text-slate-700"><strong>Option A:</strong> &quot;The font is bad and hard to see.&quot;</p>
        <p className="text-slate-700"><strong>Option B:</strong> &quot;The text in the main menu is 8px size, which is difficult to read at a normal viewing distance.&quot;</p>
      </div>
      <Textarea
        value={form.bug_report_scenario || ""}
        onChange={(e) => setForm(p => ({ ...p, bug_report_scenario: e.target.value }))}
        placeholder="Which is better and why?"
        className="input-base min-h-[80px]"
        data-testid="field-bug_report_scenario"
      />
    </div>

    <TextareaField label="8. Explain how to make buttered toast in 3-5 clear, numbered steps." value={form.buttered_toast} onChange={(v) => setForm(p => ({ ...p, buttered_toast: v }))} placeholder="Write clear, step-by-step instructions..." maxLength={2000} name="buttered_toast" />

    <TextareaField label="9. Accessibility testing often involves checking the same criteria across many different pages of a website. Do you prefer tasks that are varied and changing, or tasks that are consistent and repetitive?" value={form.task_preference} onChange={(v) => setForm(p => ({ ...p, task_preference: v }))} placeholder="Describe your preference and why..." maxLength={2000} name="task_preference" />

    <div className="space-y-1.5">
      <Label className="text-slate-700 font-medium text-sm">10. Please read the following sentence and identify the error: *</Label>
      <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
        <p className="text-slate-800 font-mono text-sm">&quot;The quick browwn fox jumps over the lazy dog.&quot;</p>
      </div>
      <Textarea
        value={form.sentence_error || ""}
        onChange={(e) => setForm(p => ({ ...p, sentence_error: e.target.value }))}
        placeholder="What is the error?"
        className="input-base min-h-[60px]"
        data-testid="field-sentence_error"
      />
    </div>
  </div>
);

// ============================================
// HR SPECIALIST QUESTIONS
// ============================================
const HRSpecialistForm = ({ form, setForm }) => (
  <div className="space-y-6">
    <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
      <h3 className="font-semibold text-slate-900 mb-2">aPHR Program Enrollment</h3>
      <p className="text-sm text-slate-600">This program prepares you for the aPHR (Associate Professional in Human Resources) certification. Please answer the following questions to the best of your ability.</p>
    </div>

    {/* Opening Questions */}
    <TextareaField label="What interests you about learning HR concepts or pursuing the aPHR certification at this point in your life or career? *" value={form.hr_interest} onChange={(v) => setForm(p => ({ ...p, hr_interest: v }))} placeholder="Tell us what draws you to HR and the aPHR certification..." maxLength={2000} name="hr_interest" required />

    <TextareaField label="What past work, volunteer, or life experience do you think will help you in this program?" value={form.past_experience} onChange={(v) => setForm(p => ({ ...p, past_experience: v }))} placeholder="Any experience you think is relevant..." maxLength={2000} name="past_experience" />

    <TextareaField label="If you complete this program successfully, what would you like to be doing next? *" value={form.ideal_next_step} onChange={(v) => setForm(p => ({ ...p, ideal_next_step: v }))} placeholder="What's your ideal outcome after completing the program?" maxLength={2000} name="ideal_next_step" required />

    {/* Scenario A */}
    <div className="space-y-1.5">
      <h3 className="text-base font-heading font-semibold text-[#0F1B3D] border-b border-slate-200 pb-2 mt-4">Scenario A</h3>
      <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 text-sm text-slate-700 italic">
        An employee sends the following message:<br /><br />
        &quot;I feel uncomfortable with comments my coworker has been making. This has been going on for a few weeks, mostly during team meetings. I do not want anyone to get in trouble. I just want it to stop.&quot;
      </div>
    </div>

    <TextareaField label="In your own words, describe what is happening in this situation. *" value={form.scenario_a_description} onChange={(v) => setForm(p => ({ ...p, scenario_a_description: v }))} placeholder="Describe what you think is happening..." maxLength={2000} name="scenario_a_description" required />

    <TextareaField label="What are the most important details in this message?" value={form.scenario_a_important_details} onChange={(v) => setForm(p => ({ ...p, scenario_a_important_details: v }))} placeholder="Identify the key details..." maxLength={2000} name="scenario_a_important_details" />

    <TextareaField label="What information is missing that would help clarify what is going on?" value={form.scenario_a_missing_info} onChange={(v) => setForm(p => ({ ...p, scenario_a_missing_info: v }))} placeholder="What else would you need to know?" maxLength={2000} name="scenario_a_missing_info" />

    <TextareaField label="What is one thing the company could do now to help address the situation?" value={form.scenario_a_action_now} onChange={(v) => setForm(p => ({ ...p, scenario_a_action_now: v }))} placeholder="Suggest an immediate action..." maxLength={2000} name="scenario_a_action_now" />

    <TextareaField label="What is one thing the company could do in the future to prevent similar situations?" value={form.scenario_a_prevention} onChange={(v) => setForm(p => ({ ...p, scenario_a_prevention: v }))} placeholder="Suggest a preventive measure..." maxLength={2000} name="scenario_a_prevention" />

    {/* Scenario B */}
    <div className="space-y-1.5">
      <h3 className="text-base font-heading font-semibold text-[#0F1B3D] border-b border-slate-200 pb-2 mt-4">Scenario B</h3>
      <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 text-sm text-slate-700 italic">
        A manager reports:<br /><br />
        &quot;Taylor has been late three times this month. Taylor does good work and is well liked. Last week they mentioned transportation problems. They were late again today but stayed late to finish their work.&quot;
      </div>
    </div>

    <TextareaField label="Summarize what is happening in one or two sentences. *" value={form.scenario_b_summary} onChange={(v) => setForm(p => ({ ...p, scenario_b_summary: v }))} placeholder="Briefly summarize the situation..." maxLength={2000} name="scenario_b_summary" required />

    <TextareaField label="Which details matter most when deciding what to do next?" value={form.scenario_b_important_details} onChange={(v) => setForm(p => ({ ...p, scenario_b_important_details: v }))} placeholder="Identify the most relevant details..." maxLength={2000} name="scenario_b_important_details" />

    <TextareaField label="Which details feel important emotionally but may not affect the decision?" value={form.scenario_b_emotional_details} onChange={(v) => setForm(p => ({ ...p, scenario_b_emotional_details: v }))} placeholder="Separate emotional factors from practical ones..." maxLength={2000} name="scenario_b_emotional_details" />

    <TextareaField label="What questions would you want answered before moving forward?" value={form.scenario_b_questions} onChange={(v) => setForm(p => ({ ...p, scenario_b_questions: v }))} placeholder="What would you need to know before acting?" maxLength={2000} name="scenario_b_questions" />

    <TextareaField label="What is one reasonable step that could be taken now?" value={form.scenario_b_next_step} onChange={(v) => setForm(p => ({ ...p, scenario_b_next_step: v }))} placeholder="Suggest a practical next step..." maxLength={2000} name="scenario_b_next_step" />

    {/* Disagreement Scenario */}
    <div className="space-y-1.5">
      <h3 className="text-base font-heading font-semibold text-[#0F1B3D] border-b border-slate-200 pb-2 mt-4">Handling Disagreements</h3>
      <p className="text-sm text-slate-500">Tell us about a time you disagreed with a coworker, supervisor, or group decision.</p>
    </div>

    <TextareaField label="What was the situation?" value={form.disagreement_situation} onChange={(v) => setForm(p => ({ ...p, disagreement_situation: v }))} placeholder="Describe the disagreement..." maxLength={2000} name="disagreement_situation" />

    <TextareaField label="How did you handle it?" value={form.disagreement_handling} onChange={(v) => setForm(p => ({ ...p, disagreement_handling: v }))} placeholder="What steps did you take?" maxLength={2000} name="disagreement_handling" />

    <TextareaField label="What was the result?" value={form.disagreement_result} onChange={(v) => setForm(p => ({ ...p, disagreement_result: v }))} placeholder="How did it turn out?" maxLength={2000} name="disagreement_result" />

    <TextareaField label="Looking back, is there anything you would handle differently now? Why or why not?" value={form.disagreement_reflection} onChange={(v) => setForm(p => ({ ...p, disagreement_reflection: v }))} placeholder="Reflect on the experience..." maxLength={2000} name="disagreement_reflection" />

    {/* Record Comparison */}
    <div className="space-y-1.5">
      <h3 className="text-base font-heading font-semibold text-[#0F1B3D] border-b border-slate-200 pb-2 mt-4">Attention to Detail</h3>
      <Label className="text-slate-700 font-medium text-sm">Please review the two records below and list all differences. *</Label>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 text-sm space-y-1">
          <p className="font-semibold text-slate-800 mb-2">Record A</p>
          <p className="text-slate-700">Employee ID: <strong>60318</strong></p>
          <p className="text-slate-700">Status: <strong>Full Time</strong></p>
          <p className="text-slate-700">Hire Date: <strong>01/09/2023</strong></p>
          <p className="text-slate-700">Department: <strong>Operations</strong></p>
        </div>
        <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 text-sm space-y-1">
          <p className="font-semibold text-slate-800 mb-2">Record B</p>
          <p className="text-slate-700">Employee ID: <strong>60381</strong></p>
          <p className="text-slate-700">Status: <strong>Full Time</strong></p>
          <p className="text-slate-700">Hire Date: <strong>01/09/2023</strong></p>
          <p className="text-slate-700">Department: <strong>Operations</strong></p>
        </div>
      </div>
      <Textarea
        value={form.record_differences || ""}
        onChange={(e) => setForm(p => ({ ...p, record_differences: e.target.value }))}
        placeholder="List all the differences you find between Record A and Record B..."
        className="input-base min-h-[80px]"
        data-testid="field-record_differences"
      />
    </div>

    <TextareaField label="Why can small details like this matter in a real workplace?" value={form.detail_importance} onChange={(v) => setForm(p => ({ ...p, detail_importance: v }))} placeholder="Explain why attention to detail matters in HR..." maxLength={2000} name="detail_importance" />

    {/* Learning Preferences */}
    <div className="space-y-1.5">
      <h3 className="text-base font-heading font-semibold text-[#0F1B3D] border-b border-slate-200 pb-2 mt-4">Learning Preferences</h3>
    </div>

    <CheckboxGroup label="When learning something new, what helps you most? (select all that apply)" options={[
      "Reading", "Watching videos", "Real world examples",
      "Discussion", "Practice questions", "Teaching others"
    ]} value={form.learning_methods || []} onChange={(v) => setForm(p => ({ ...p, learning_methods: v }))} name="learning_methods" />

    <TextareaField label="What usually makes learning or studying difficult for you?" value={form.learning_difficulty} onChange={(v) => setForm(p => ({ ...p, learning_difficulty: v }))} placeholder="Describe any challenges you face when learning..." maxLength={2000} name="learning_difficulty" />

    <TextareaField label="When you feel overwhelmed by a lot of information, what do you typically do?" value={form.overwhelm_response} onChange={(v) => setForm(p => ({ ...p, overwhelm_response: v }))} placeholder="How do you cope with information overload?" maxLength={2000} name="overwhelm_response" />

    <TextareaField label="What is one skill you hope to strengthen through this program?" value={form.skill_to_strengthen} onChange={(v) => setForm(p => ({ ...p, skill_to_strengthen: v }))} placeholder="What skill do you most want to develop?" maxLength={2000} name="skill_to_strengthen" />
  </div>
);

// ============================================
// DEFAULT / GENERIC QUESTIONS (for other programs)
// ============================================
const GenericForm = ({ form, setForm, programName }) => (
  <div className="space-y-6">
    <TextareaField label={`Why are you interested in ${programName || 'this program'}?`} value={form.interest_reason} onChange={(v) => setForm(p => ({ ...p, interest_reason: v }))} placeholder="Tell us what draws you to this program..." maxLength={2000} name="interest_reason" required />

    <TextareaField label="Describe a technical problem you've solved or a project you're proud of" value={form.problem_solved} onChange={(v) => setForm(p => ({ ...p, problem_solved: v }))} placeholder="It doesn't have to be tech-related..." maxLength={2000} name="problem_solved" />

    <TextareaField label="What is your ideal outcome from completing this program?" value={form.ideal_outcome} onChange={(v) => setForm(p => ({ ...p, ideal_outcome: v }))} placeholder="What do you hope to achieve?" maxLength={2000} name="ideal_outcome" />

    <SelectField label="What best describes your learning style?" options={[
      { value: "visual", label: "Visual - I learn best by seeing" },
      { value: "hands_on", label: "Hands-on - I learn best by doing" },
      { value: "reading", label: "Reading/Writing - I learn best from text" },
      { value: "auditory", label: "Auditory - I learn best by listening" },
      { value: "mixed", label: "Mixed - I use multiple methods" },
    ]} value={form.learning_style} onChange={(v) => setForm(p => ({ ...p, learning_style: v }))} name="learning_style" />

    <SelectField label="How do you typically react when stuck on a problem?" options={[
      { value: "research", label: "Research solutions online" },
      { value: "ask_help", label: "Ask someone for help" },
      { value: "break", label: "Take a break and come back later" },
      { value: "persist", label: "Keep trying different approaches" },
      { value: "combination", label: "A combination of these" },
    ]} value={form.problem_reaction} onChange={(v) => setForm(p => ({ ...p, problem_reaction: v }))} name="problem_reaction" />

    <TextareaField label="Have you taken any relevant courses or training before?" value={form.prior_training} onChange={(v) => setForm(p => ({ ...p, prior_training: v }))} placeholder="Online courses, bootcamps, certifications, etc." name="prior_training" />

    <TextareaField label="Tell us about any work experience you have" value={form.work_experience} onChange={(v) => setForm(p => ({ ...p, work_experience: v }))} placeholder="Any work, volunteer, or project experience..." maxLength={2000} name="work_experience" />

    <CheckboxGroup label="What type of support would help you succeed? (select all)" options={[
      "Extra time on assignments", "Written instructions", "1-on-1 tutoring",
      "Study groups", "Recorded lectures", "Break reminders",
      "Career coaching", "Mental health support"
    ]} value={form.support_needed || []} onChange={(v) => setForm(p => ({ ...p, support_needed: v }))} name="support_needed" />

    <TextareaField label="Is there anything else you'd like us to know?" value={form.additional_info} onChange={(v) => setForm(p => ({ ...p, additional_info: v }))} placeholder="Optional..." name="additional_info" />
  </div>
);

// ============================================
// SHARED FIELD COMPONENTS
// ============================================
const TextareaField = ({ label, value, onChange, placeholder, maxLength, name, required }) => (
  <div className="space-y-1.5">
    <Label className="text-slate-700 font-medium text-sm">{label} {required && <span className="text-red-400">*</span>}</Label>
    <Textarea
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      maxLength={maxLength}
      className="input-base min-h-[80px]"
      data-testid={`field-${name}`}
    />
    {maxLength && <p className="text-xs text-slate-400 text-right">{(value || "").length} / {maxLength}</p>}
  </div>
);

const SelectField = ({ label, options, value, onChange, name, required }) => (
  <div className="space-y-1.5">
    <Label className="text-slate-700 font-medium text-sm">{label} {required && <span className="text-red-400">*</span>}</Label>
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger className="input-base" data-testid={`field-${name}`}><SelectValue placeholder="Select..." /></SelectTrigger>
      <SelectContent className="bg-white border-slate-200">
        {options.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

const CheckboxGroup = ({ label, options, value, onChange, name }) => (
  <div className="space-y-2">
    <Label className="text-slate-700 font-medium text-sm">{label}</Label>
    <div className="grid sm:grid-cols-2 gap-2">
      {options.map((opt) => {
        const checked = (value || []).includes(opt);
        return (
          <label key={opt} className={`flex items-center gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-colors text-sm ${
            checked ? "border-brand-gold/40 bg-brand-gold/5" : "border-slate-200 hover:border-slate-300"
          }`}>
            <Checkbox
              checked={checked}
              onCheckedChange={(c) => {
                const updated = c ? [...(value || []), opt] : (value || []).filter(v => v !== opt);
                onChange(updated);
              }}
              data-testid={`field-${name}-${opt.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
            />
            <span className="text-slate-700">{opt}</span>
          </label>
        );
      })}
    </div>
  </div>
);

// ============================================
// PROGRAM ICON MAP
// ============================================
const PROGRAM_ICONS = {
  "Cybersecurity": Shield,
  "Quality Assurance": BookOpen,
  "IT Foundations": Monitor,
  "Accessibility": Accessibility,
  "HR Specialist": UserCheck,
};

// Map program names to their specific form components
const PROGRAM_FORMS = {
  "Cybersecurity": CybersecurityForm,
  "Quality Assurance": QualityAssuranceForm,
  "IT Foundations": ITFoundationsForm,
  "Accessibility": AccessibilityForm,
  "HR Specialist": HRSpecialistForm,
};

// ============================================
// MAIN COMPONENT
// ============================================
const ProgramApplicationStep = ({ programId, programName, onSubmit, onBack, submitting, existingData }) => {
  const [form, setForm] = useState(existingData || {});
  const Icon = PROGRAM_ICONS[programName] || GraduationCap;
  const FormComponent = PROGRAM_FORMS[programName];

  useEffect(() => {
    if (existingData) setForm(existingData);
  }, [existingData]);

  const handleSubmit = () => {
    const data = {
      ...form,
      program_id: programId,
      program_name: programName,
      submitted_at: new Date().toISOString(),
    };
    onSubmit(data);
  };

  const programDescriptions = {
    "Cybersecurity": "We're excited you're interested in our Cybersecurity Academy!",
    "Quality Assurance": "Tell us about your interest in Quality Assurance testing.",
    "IT Foundations": "Tell us about your interest in learning IT fundamentals.",
    "Accessibility": "Tell us about your interest in Digital Accessibility.",
    "HR Specialist": "Tell us about your interest in the aPHR certification program.",
  };

  return (
    <Card className="card-light overflow-hidden">
      <div className="bg-gradient-to-r from-[#1E3A8A] to-[#152C6E] p-6 text-white">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-heading font-bold">
              {programName || "Program"} Application
            </h2>
            <p className="text-white/70 text-sm">
              {programDescriptions[programName] || `Tell us about your interest in ${programName || "this program"}.`}
            </p>
          </div>
        </div>
      </div>

      {FormComponent === CybersecurityForm && (
        <div className="bg-brand-gold/5 border-b border-brand-gold/20 px-6 py-3">
          <p className="text-sm text-slate-600">
            This form helps us understand your goals, match you with the right cohort, and provide appropriate accommodations.
          </p>
        </div>
      )}

      <CardContent className="pt-6">
        {FormComponent ? (
          <FormComponent form={form} setForm={setForm} />
        ) : (
          <GenericForm form={form} setForm={setForm} programName={programName} />
        )}

        <div className="flex justify-between pt-8">
          <Button variant="outline" onClick={onBack} className="btn-outline flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> Back
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="btn-primary flex items-center gap-2"
            data-testid="submit-enrollment"
          >
            {submitting ? (
              <><div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /> Submitting...</>
            ) : (
              <><Send className="w-4 h-4" /> Submit Application</>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProgramApplicationStep;
